import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExperiment } from '../../contexts/ExperimentContext';
import { useTracker } from '../../contexts/TrackerContext';
import { savePhaseData } from '../../services/api';
import MicroSurvey from '../common/MicroSurvey';
import './CodingTask.css';
import {
  codingScenarios,
  getScenariosByCongruence,
  balanceScenarios,
  CodingScenario
} from '../../data/codingScenariosWithCongruence';
import { SeededRandom, generateOrderSeed } from '../../utils/seededRandom';

interface ExtendedCodingScenario extends CodingScenario {
  decision?: 'use' | 'skip' | null;
}

const CodingTask: React.FC = () => {
  const navigate = useNavigate();
  const { experimentData, updateProgress } = useExperiment();
  const { tracker, trackHover, trackMouseMovement } = useTracker();
  
  const [scenarios, setScenarios] = useState<ExtendedCodingScenario[]>([]);
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [showMicroSurvey, setShowMicroSurvey] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    scenario: ExtendedCodingScenario, 
    actionTaken: 'use' | 'skip'
  } | null>(null);
  
  const phaseStartTime = useRef(Date.now());
  const scenarioStartTime = useRef(Date.now());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tracker.track('phaseStart', {
      phase: 'coding-task',
      condition: experimentData?.participant?.conditions || {},
      congruenceCondition: 'balanced-2x2x2x2' // All participants receive balanced distribution
    });

    // Generate coding scenarios based on balanced 2×2×2×2 design
    const generatedScenarios = generateCodingScenarios(experimentData?.participant?.id);
    setScenarios(generatedScenarios);
    
    return () => {
      tracker.track('phaseEnd', {
        phase: 'coding-task',
        duration: Date.now() - phaseStartTime.current
      });
    };
  }, [tracker, experimentData]);

  useEffect(() => {
    scenarioStartTime.current = Date.now();
  }, [currentScenarioIndex]);

  const generateCodingScenarios = (participantId?: string): ExtendedCodingScenario[] => {
    // Get balanced scenarios with Latin Square counterbalancing (2×2 design: Congruence × Truth)
    // Algorithm guarantees: 1 Congruent-Safe, 1 Congruent-Risky, 1 Incongruent-Safe, 1 Incongruent-Risky (4 total)
    // Domains rotate across participants for cross-participant balance
    let selectedScenarios = getScenariosByCongruence(participantId);
    selectedScenarios = balanceScenarios(selectedScenarios);
    
    // Remove duplicates and add decision field
    const uniqueScenarios = selectedScenarios.filter((scenario, index, self) =>
      index === self.findIndex(s => s.id === scenario.id)
    );
    
    const scenarios: ExtendedCodingScenario[] = uniqueScenarios.map(scenario => ({
      ...scenario,
      decision: null
    }));
    
    // Randomize order with seeded RNG for reproducibility
    const orderSeed = generateOrderSeed(participantId || 'unknown', {
      taskType: 'coding',
      timestamp: Date.now(),
      congruence: 'balanced-2x2x2x2'
    });
    const rng = new SeededRandom(orderSeed);
    return rng.shuffle(scenarios);
  };

  const handleAction = (scenario: ExtendedCodingScenario, actionTaken: 'use' | 'skip') => {
    if (scenario.decision) {
      return; // Already processed
    }

    // Track the initial action
    tracker.track('codingDecision', {
      scenarioId: scenario.id,
      scenarioTitle: scenario.title,
      domain: scenario.domain, // ADDED: Counterbalancing domain (CDom1-CDom8)
      actionTaken,
      isRisky: scenario.isRisky,
      congruence: scenario.congruence,
      responseTime: Date.now() - scenarioStartTime.current,
      scenarioType: scenario.type,
      source: scenario.source,
      presentationOrder: currentScenarioIndex // order in which this snippet was shown
    });

    // Show micro-survey
    setPendingAction({ scenario, actionTaken });
    setShowMicroSurvey(true);
  };

  const handleMicroSurveySubmit = (surveyResponse: {
    reason: string;
    customReason?: string;
    reasonCategory?: 'gist' | 'verbatim' | 'other' | 'task_need' | 'external_pressure' | 'motivation' | 'risk_assessment';
    confidence?: number;
    primaryReasonKeySelected?: string;
    routeSelected?: 'gist' | 'verbatim';
    gistOrder?: string[];
    verbatimOrder?: string[];
    selectedIndexWithinColumn?: number;
    decisionLatencyMs?: number;
    totalMicroSurveyTimeMs?: number;
    step1TimeMs?: number | null;
    step2TimeMs?: number | null;
  }) => {
    if (pendingAction) {
      // Track survey response with consistent event name
      tracker.track('microSurveyResponse', {
        taskType: 'coding',
        scenarioId: pendingAction.scenario.id,
        actionTaken: pendingAction.actionTaken,
        response: surveyResponse.reason,
        customReason: surveyResponse.customReason,
        reasonCategory: surveyResponse.reasonCategory,
        confidence: surveyResponse.confidence, // FIXED: Include confidence
        primaryReasonKeySelected: surveyResponse.primaryReasonKeySelected,
        routeSelected: surveyResponse.routeSelected,
        gistOrder: surveyResponse.gistOrder,
        verbatimOrder: surveyResponse.verbatimOrder,
        selectedIndexWithinColumn: surveyResponse.selectedIndexWithinColumn,
        decisionLatencyMs: surveyResponse.decisionLatencyMs,
        totalMicroSurveyTimeMs: surveyResponse.totalMicroSurveyTimeMs,
        step1TimeMs: surveyResponse.step1TimeMs,
        step2TimeMs: surveyResponse.step2TimeMs,
        isRisky: pendingAction.scenario.isRisky,
        congruence: pendingAction.scenario.congruence,
        gistCues: pendingAction.scenario.gistCues,
        verbatimCues: pendingAction.scenario.verbatimCues,
        conflictLevel: pendingAction.scenario.conflictLevel,
        phase: 'coding-task'
      });

      // Update scenario decision
      const updatedScenarios = scenarios.map(s =>
        s.id === pendingAction.scenario.id
          ? { ...s, decision: pendingAction.actionTaken }
          : s
      );
      setScenarios(updatedScenarios);

      // localStorage backup after each decision — last-resort copy that survives a
      // failed/blocked network until the phase save can flush (mirrors info-sharing).
      try {
        const pid = experimentData?.participant?.id || localStorage.getItem('participantId');
        if (pid) localStorage.setItem(`codingBackup_${pid}`, JSON.stringify({
          endTime: Date.now(),
          decisions: updatedScenarios
            .filter(s => s.decision)
            .map(s => ({ scenarioId: s.id, decision: s.decision, isRisky: s.isRisky, congruence: s.congruence }))
        }));
      } catch (e) { console.error('Could not write coding backup:', e); }

      // Move to next scenario or complete
      if (currentScenarioIndex < scenarios.length - 1) {
        setCurrentScenarioIndex(currentScenarioIndex + 1);
      } else {
        handleComplete();
      }
    }

    setShowMicroSurvey(false);
    setPendingAction(null);
  };

  const handleComplete = async () => {
    const codingDecisions = scenarios.map((s, i) => ({
      scenarioId: s.id,
      decision: s.decision,
      isRisky: s.isRisky,
      congruence: s.congruence,
      presentationOrder: i // order in which this snippet was shown
    }));

    const phaseData = {
      duration: Date.now() - phaseStartTime.current,
      totalScenarios: scenarios.length,
      decisions: codingDecisions,
      riskyAccepted: scenarios.filter(s => s.isRisky && s.decision === 'use').length,
      safeRejected: scenarios.filter(s => !s.isRisky && s.decision === 'skip').length,
      completedAt: new Date().toISOString()
    };

    tracker.track('phaseComplete', {
      phase: 'coding-task',
      ...phaseData
    });

    // localStorage backup BEFORE navigation — last-resort copy until the save confirms.
    const participantId = experimentData?.participant?.id || localStorage.getItem('participantId');
    try {
      if (participantId) localStorage.setItem(`codingBackup_${participantId}`, JSON.stringify(phaseData));
    } catch (e) { console.error('Could not write coding backup:', e); }

    // CRITICAL: Save coding task data to database
    try {
      if (participantId) {
        await savePhaseData(participantId, 'coding-task', phaseData);
        console.log('Coding task data saved to database');
        // Confirmed persisted — safe to clear the backup.
        try { localStorage.removeItem(`codingBackup_${participantId}`); } catch {}
      }
    } catch (error) {
      console.error('Failed to save coding task data (backup retained for recovery):', error);
    }

    // Navigate based on two-cohort system with randomized task order
    const taskAssignment = experimentData?.participant?.taskAssignment;
    const cohort = taskAssignment?.cohort;
    const taskOrder = taskAssignment?.taskOrder;

    console.log('CodingTask completion navigation:', {
      cohort: cohort,
      taskOrder: taskOrder,
      taskAssignment: taskAssignment
    });

    if (cohort === 'email-coding') {
      if (taskOrder === 'secondary-first') {
        // Coding came first, email is next
        updateProgress(60); // 60% complete after first task
        navigate('/filler-task'); // Go to filler task before email task
      } else {
        // taskOrder === 'email-first' means email came first, coding is second, we're done
        updateProgress(70); // 70% complete after second task
        navigate('/post-experiment'); // Complete - go to post-experiment
      }
    } else {
      // This shouldn't happen for coding task in email-device cohort
      console.warn('CodingTask completed but participant not in email-coding cohort');
      navigate('/post-experiment');
    }
  };

  const currentScenario = scenarios[currentScenarioIndex];

  if (!currentScenario) {
    return <div>Loading scenarios...</div>;
  }

  const getRiskIndicatorLabel = (indicator: string): string => {
    const labels: { [key: string]: string } = {
      'unknown-package': 'Unknown package',
      'mining-endpoint': 'Crypto mining endpoint',
      'suspicious-url': 'Suspicious URL',
      'typosquatting': 'Misspelled package name',
      'postinstall-script': 'Runs scripts on install',
      'data-exfiltration': 'Sends data externally',
      'exposed-ports': 'Exposed to internet',
      'weak-password': 'Weak credentials',
      'secret-in-logs': 'Secrets visible in logs',
      'environment-variables': 'Uses secure env vars',
      'official-library': 'Official library',
      'secure-pattern': 'Secure coding pattern',
      'official-docs': 'From official documentation',
      'proper-cleanup': 'Proper resource cleanup',
      'company-package': 'Internal company package',
      'auth-check': 'Has authentication',
      'cors-configured': 'CORS properly configured'
    };
    return labels[indicator] || indicator;
  };

  return (
    <div className="coding-task" ref={containerRef} onMouseMove={trackMouseMovement}>
      <div className="coding-header">
        <h2>Code Review Task</h2>
        <div className="progress-indicator">
          Scenario {currentScenarioIndex + 1} of {scenarios.length}
        </div>
      </div>

      <div className="coding-scenario">
        <div className="scenario-header">
          <h3>{currentScenario.title}</h3>
          <span className="scenario-source">Source: {currentScenario.source}</span>
        </div>

        <div className="scenario-context">
          <p className="context-label">Context:</p>
          <p>{currentScenario.context}</p>
        </div>

        <div className="scenario-description">
          <p>{currentScenario.description}</p>
        </div>

        {currentScenario.codeSnippet && (
          <div className="code-container">
            <div className="code-header">
              <span className="code-label">Code:</span>
              <span className="code-type">{currentScenario.type}</span>
            </div>
            <pre className="code-snippet">
              <code>{currentScenario.codeSnippet}</code>
            </pre>
          </div>
        )}



        <div className="action-container">
          <p className="action-prompt">Would you use this code in your project?</p>
          <div className="action-buttons">
            <button
              className="btn btn-use"
              onClick={() => handleAction(currentScenario, 'use')}
              disabled={currentScenario.decision !== null}
            >
              <span className="btn-icon">✓</span>
              Use This Code
            </button>
            <button
              className="btn btn-skip"
              onClick={() => handleAction(currentScenario, 'skip')}
              disabled={currentScenario.decision !== null}
            >
              <span className="btn-icon">✗</span>
              Skip / Find Alternative
            </button>
          </div>
        </div>
      </div>

      {showMicroSurvey && pendingAction && (
        <MicroSurvey
          taskType="coding"
          scenarioType={pendingAction.scenario.type}
          actionTaken={pendingAction.actionTaken === 'use' ? 'proceed' : 'decline'}
          onSubmit={handleMicroSurveySubmit}
          onClose={() => {
            setShowMicroSurvey(false);
            setPendingAction(null); // Clear pending action to prevent state issues
          }}
        />
      )}
    </div>
  );
};

export default CodingTask;
